/**
 * Telephony domain.
 *
 * Everything about calls and texts that both the server and the office UI need
 * to agree on: what a call looks like, what events a provider can report, and —
 * the part with real logic in it — how an inbound number becomes "Dana
 * Whitlock, job #1042, tech en route, $168 outstanding" on a dispatcher's
 * screen before they pick up.
 *
 * Provider-agnostic on purpose. Nothing here knows what Simply Call is; the
 * adapter maps its payloads onto these shapes.
 */
import { normalizePhone } from './format.js';
// --- vocabulary -------------------------------------------------------------
export const CALL_DIRECTIONS = ['inbound', 'outbound'];
/**
 * The lifecycle of a call. `ringing` and `in_progress` are live states — the
 * screen-pop watches for those; the rest are terminal.
 */
export const CALL_STATUSES = [
    'ringing',
    'in_progress',
    'completed',
    'missed',
    'voicemail',
    'failed',
];
export const LIVE_CALL_STATUSES = ['ringing', 'in_progress'];
export const CALL_STATUS_LABELS = {
    ringing: 'Ringing',
    in_progress: 'On the call',
    completed: 'Completed',
    missed: 'Missed',
    voicemail: 'Voicemail',
    failed: 'Failed',
};
export const MESSAGE_DIRECTIONS = ['inbound', 'outbound'];
export const MESSAGE_STATUSES = [
    'queued',
    'sent',
    'delivered',
    'received',
    'failed',
];
export const MESSAGE_STATUS_LABELS = {
    queued: 'Queued',
    sent: 'Sent',
    delivered: 'Delivered',
    received: 'Received',
    failed: 'Failed',
};
export function isCallEvent(event) {
    return event.type.startsWith('call.');
}
/** Which stored status an event implies. Ordered so a late event cannot regress. */
const STATUS_RANK = {
    ringing: 0,
    in_progress: 1,
    voicemail: 2,
    missed: 2,
    failed: 2,
    completed: 3,
};
/**
 * Fold an event onto a call's current status.
 *
 * Providers reorder webhooks under load, so `answered` can arrive after
 * `ended`. Ranking prevents a completed call from being dragged back to
 * ringing, which would light up a screen-pop for a call that finished minutes
 * ago.
 */
export function nextCallStatus(current, event) {
    let candidate = null;
    if (event.type === 'call.started')
        candidate = 'ringing';
    else if (event.type === 'call.answered')
        candidate = 'in_progress';
    else if (event.type === 'call.ended') {
        candidate = event.disposition === 'completed' ? 'completed' : event.disposition;
    }
    if (!candidate)
        return current;
    if (!current)
        return candidate;
    return STATUS_RANK[candidate] >= STATUS_RANK[current] ? candidate : current;
}
/**
 * Compare two numbers the way a person would.
 *
 * `+1 (512) 555-0142`, `512-555-0142` and `15125550142` are the same phone.
 * Comparing raw strings misses that; comparing full digit strings misses the
 * country code. So: exact digits first, then the last ten, which is the
 * longest suffix that is still unambiguous in NANP and good enough elsewhere.
 */
export function phoneKeys(phone) {
    const full = normalizePhone(phone);
    return { full, national: full.length > 10 ? full.slice(-10) : full };
}
export function samePhone(a, b) {
    const ka = phoneKeys(a);
    const kb = phoneKeys(b);
    if (!ka.full || !kb.full)
        return false;
    if (ka.full === kb.full)
        return true;
    return ka.national.length >= 7 && ka.national === kb.national;
}
/**
 * Resolve an inbound number to a customer, and then to the job they are most
 * likely calling about.
 *
 * Job selection is deliberately opinionated, in this order:
 *   1. a job in progress right now — the tech is standing in their kitchen;
 *   2. a job scheduled today;
 *   3. the next scheduled job within the lookahead;
 *   4. the most recent job within the lookbehind — "you were here Tuesday".
 * An unpaid invoice does not pull a job forward; that context is shown
 * alongside rather than guessed at.
 */
export function matchCaller(fromNumber, customers, jobs = [], options = {}) {
    const now = options.now ?? new Date();
    const lookaheadMs = (options.lookaheadDays ?? 14) * 86_400_000;
    const lookbehindMs = (options.lookbehindDays ?? 30) * 86_400_000;
    const caller = phoneKeys(fromNumber);
    if (!caller.full)
        return { customer: null, job: null, confidence: 'none', candidates: [] };
    const exact = [];
    const national = [];
    for (const customer of customers) {
        const numbers = [customer.phone, ...(customer.altPhones ?? [])];
        let matchedExact = false;
        let matchedNational = false;
        for (const number of numbers) {
            const key = phoneKeys(number);
            if (!key.full)
                continue;
            if (key.full === caller.full)
                matchedExact = true;
            else if (key.national.length >= 7 && key.national === caller.national)
                matchedNational = true;
        }
        if (matchedExact)
            exact.push(customer);
        else if (matchedNational)
            national.push(customer);
    }
    const pool = exact.length > 0 ? exact : national;
    if (pool.length === 0) {
        return { customer: null, job: null, confidence: 'none', candidates: [] };
    }
    // More than one customer on the same number is normal — a landlord and a
    // tenant, a couple, a property manager. Do not guess; show the choice.
    if (pool.length > 1) {
        return { customer: null, job: null, confidence: 'ambiguous', candidates: pool };
    }
    const customer = pool[0];
    const theirJobs = jobs.filter((j) => j.customerId === customer.id);
    return {
        customer,
        job: pickLikelyJob(theirJobs, now, lookaheadMs, lookbehindMs),
        confidence: exact.length > 0 ? 'exact' : 'national',
        candidates: pool,
    };
}
/**
 * Which of a customer's jobs is this call about?
 *
 * Exported because the screen-pop needs the SAME answer the server arrived at.
 * Left to its own devices the UI would show whichever job happened to sort
 * first, which is how a dispatcher ends up looking at a job that was paid three
 * weeks ago while the customer is asking about the van outside.
 */
export function pickJobForCall(jobs, options = {}) {
    return pickLikelyJob(jobs, options.now ?? new Date(), (options.lookaheadDays ?? 14) * 86_400_000, (options.lookbehindDays ?? 30) * 86_400_000);
}
function pickLikelyJob(jobs, now, lookaheadMs, lookbehindMs) {
    if (jobs.length === 0)
        return null;
    const live = jobs.find((j) => j.status === 'in_progress');
    if (live)
        return live;
    const withTime = jobs
        .filter((j) => j.scheduledStart)
        .map((j) => ({ job: j, at: new Date(j.scheduledStart).getTime() }))
        .filter((entry) => Number.isFinite(entry.at));
    const nowMs = now.getTime();
    const isSameDay = (a) => {
        const d = new Date(a);
        return (d.getFullYear() === now.getFullYear() &&
            d.getMonth() === now.getMonth() &&
            d.getDate() === now.getDate());
    };
    const today = withTime
        .filter((e) => isSameDay(e.at))
        .sort((a, b) => Math.abs(a.at - nowMs) - Math.abs(b.at - nowMs));
    if (today[0])
        return today[0].job;
    const upcoming = withTime
        .filter((e) => e.at > nowMs && e.at - nowMs <= lookaheadMs)
        .sort((a, b) => a.at - b.at);
    if (upcoming[0])
        return upcoming[0].job;
    const recent = withTime
        .filter((e) => e.at < nowMs && nowMs - e.at <= lookbehindMs)
        .sort((a, b) => b.at - a.at);
    if (recent[0])
        return recent[0].job;
    return null;
}
// --- display ----------------------------------------------------------------
export function formatCallDuration(seconds) {
    if (seconds == null || seconds < 0)
        return '—';
    if (seconds < 60)
        return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const rest = seconds % 60;
    if (minutes < 60)
        return `${minutes}m ${String(rest).padStart(2, '0')}s`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ${String(minutes % 60).padStart(2, '0')}m`;
}
/** One line summarising a call for a timeline entry. */
export function describeCall(call) {
    const who = call.handledByName ? ` · ${call.handledByName}` : '';
    if (call.status === 'missed') {
        return call.direction === 'inbound' ? `Missed call${who}` : `No answer${who}`;
    }
    if (call.status === 'voicemail')
        return `Left voicemail${who}`;
    if (call.status === 'failed')
        return `Call failed${who}`;
    if (call.status === 'ringing')
        return call.direction === 'inbound' ? 'Incoming call' : 'Dialling…';
    if (call.status === 'in_progress')
        return `On the call${who}`;
    const verb = call.direction === 'inbound' ? 'Inbound call' : 'Outbound call';
    return `${verb} · ${formatCallDuration(call.durationSeconds)}${who}`;
}
/** SMS segment count, so the office can see when a long text costs three. */
export function smsSegments(body) {
    if (body.length === 0)
        return 0;
    // GSM-7 vs UCS-2: any character outside the basic set forces the whole
    // message into 16-bit encoding and shortens every segment.
    const unicode = /[^ -@£$¥èéùìòÇØøÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ¤¡ÄÖÑÜ§¿äöñüà]/.test(body);
    const single = unicode ? 70 : 160;
    const multi = unicode ? 67 : 153;
    return body.length <= single ? 1 : Math.ceil(body.length / multi);
}
//# sourceMappingURL=telephony.js.map